import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Dropdown,
  H4,
  Input,
  L16,
  L88,
  Text,
  TextArea,
  Stack,
  H5,
  Message,
  Spinner,
  Inline,
  Checkbox,
  Radio
} from "@adfenix/alexandria";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { Col, Row } from "react-grid-system";

import Divider from "components/Divider";
import { Modal } from "components/Modal";
import { Table } from "components/Datatable";
import styled from "styled-components";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAvailableAttributes } from "api/services/programService";
import { useParams } from "react-router-dom";
import { GenericError } from "components/GenericError";
import Page from "components/Page";
import { ProgramService } from "@adfenix/types";
import { useDisabledFields } from "./useDisabledFields";
import { customerFacingPublishingMode } from "../../../../utils/publishingMode";
import { useTranslation } from "react-i18next";

const And = styled.div<{ firstChild: boolean }>`
  position: absolute;
  top: -9px; // Half of line height, which is 72
  background: ${({ theme }) => L88(theme.colors.grey)};
  color: ${({ theme }) => L16(theme.colors.grey)};
  border-radius: 50px;
  padding: 2px 8px;
  font-weight: 700;
  font-size: 12px;
  display: ${({ firstChild }) => (firstChild ? "none" : undefined)};
`;

const StyledInput = styled(Input)`
  width: 80px;
  height: 24px;
  margin-left: auto;
  border: "1px solid #cfd7e3";
  text-align: "center";
`;

export const PublishingRules = () => {
  const { t } = useTranslation();

  const disabledFields = useDisabledFields();
  const { register, watch, setValue, resetField } =
    useFormContext<ProgramService.ProgramModel>();
  const [showModal, setShowModal] = useState(false);
  const [conditionIndex, setConditionIndex] = useState(0);
  const [addedTheNewRule, setAddedTheNewRule] = useState(false);
  const [recentlyAdded, setRecentlyAdded] = useState<number[]>([]);

  const { programId } = useParams<{ programId: string }>();

  const {
    data: publishingAttributes,
    isLoading,
    isError
  } = useQuery(["getAvailableAttributes", programId], () =>
    getAvailableAttributes(programId!)
  );

  const {
    publishingMode,
    conditions,
    conditionCheckDelayInDays,
    publicationDelayInHours,
    publicationCancelable,
    hideOrderPageUntilPublishingConditionsAreMet
  } = watch();

  const { fields, append, remove } = useFieldArray<
    Partial<ProgramService.ProgramModel>
  >({
    name: "conditions"
  });

  const publishingModes = [
    {
      id: "MachineAutoPublish",
      name: customerFacingPublishingMode("MachineAutoPublish", t)
    },
    {
      id: "Manual",
      name: customerFacingPublishingMode("Manual", t)
    },
    {
      id: "PremiumAutoPublish",
      name: customerFacingPublishingMode("PremiumAutoPublish", t)
    },
    {
      id: "SellerPaid",
      name: customerFacingPublishingMode("SellerPaid", t)
    }
  ];

  const publishingOperators: ProgramService.ConditionOperatorType[] = [
    "EqualToAny",
    "EqualToNone",
    "EqualTo",
    "NotEquals",
    "GreaterThan",
    "LessThan",
    "ListContains",
    "ListContainsAny",
    "DateAfter",
    "DateBefore"
  ];

  const addToNewlyAddedList = (index: number) => {
    if (!recentlyAdded.includes(index))
      setRecentlyAdded([...recentlyAdded, conditionIndex]);
  };

  const removeFromNewlyAddedList = (index: number) => {
    if (recentlyAdded.includes(index))
      setRecentlyAdded(recentlyAdded.filter(index => index != index));
  };

  const closeModal = () => {
    setShowModal(false);
    /// if user didn't click on edit/add button
    if (!addedTheNewRule && !recentlyAdded.includes(conditionIndex)) {
      /// if this is a new record that has not been saved to database
      if (conditions?.[conditionIndex]?.id) {
        resetField(`conditions.${conditionIndex}`);
      } else remove(conditionIndex);
    }

    setAddedTheNewRule(false);
  };

  useEffect(() => {
    if (addedTheNewRule) closeModal();
  }, [addedTheNewRule, closeModal]);

  if (isLoading)
    return (
      <Page>
        <Spinner />
      </Page>
    );

  if (isError || !publishingAttributes) return <GenericError />;

  return (
    <Card>
      {showModal && (
        <Modal show={true} onClose={() => closeModal()}>
          <Modal.Card>
            <Modal.Card.Head onClose={() => closeModal()}>
              <Modal.Card.Title>
                {conditions?.[conditionIndex]?.id ||
                recentlyAdded.includes(conditionIndex)
                  ? t(
                      "programBuilder.program.details.publishingRule.header.edit"
                    )
                  : t(
                      "programBuilder.program.details.publishingRule.header.add"
                    )}
              </Modal.Card.Title>
            </Modal.Card.Head>

            <Modal.Card.Body>
              <form>
                <Stack spacing={"m"}>
                  <Row>
                    <Col>
                      <Controller
                        name={`conditions.${conditionIndex}.attribute`}
                        render={({ field }) => (
                          <Dropdown
                            {...field}
                            placeholder={t(
                              "programBuilder.program.details.publishingRule.attribute.placeholder"
                            )}
                            valueObjectIndexAccessor={"name"}
                            items={publishingAttributes}
                            onChange={item => {
                              field.onChange(item.name),
                                setValue(
                                  `conditions.${conditionIndex}.subjectType`,
                                  item.subjectType
                                );
                            }}
                            label={t(
                              "programBuilder.program.details.publishingRule.attribute.label"
                            )}
                            style={{ marginBottom: "8px" }}
                            disabled={disabledFields("conditions.attribute")}
                          />
                        )}
                      />

                      <Controller
                        name={`conditions.${conditionIndex}.subjectType`}
                        render={({ field }) => (
                          <Dropdown
                            {...field}
                            valueObjectIndexAccessor="subjectType"
                            items={[...new Set(publishingAttributes)]}
                            placeholder={t(
                              "programBuilder.program.details.publishingRule.subjectType.placeholder"
                            )}
                            label={t(
                              "programBuilder.program.details.publishingRule.subjectType.label"
                            )}
                            disabled
                          />
                        )}
                      />
                    </Col>

                    <Col>
                      <Controller
                        name={`conditions.${conditionIndex}.operator`}
                        render={({ field }) => (
                          <Dropdown
                            items={publishingOperators}
                            {...field}
                            placeholder={t(
                              "programBuilder.program.details.publishingRule.operator.placeholder"
                            )}
                            label={t(
                              "programBuilder.program.details.publishingRule.operator.label"
                            )}
                            disabled={disabledFields("conditions.operator")}
                          />
                        )}
                      />
                    </Col>
                  </Row>

                  {conditions
                    ? (conditions[conditionIndex].operator === "EqualToAny" ||
                        conditions[conditionIndex].operator === "EqualToNone" ||
                        conditions[conditionIndex].operator ===
                          "ListContainsAny") && (
                        <Message>
                          {t(
                            "programBuilder.program.details.publishingRule.rule.message"
                          )}
                        </Message>
                      )
                    : null}

                  <TextArea
                    rows={8}
                    label={t(
                      "programBuilder.program.details.publishingRule.value.label"
                    )}
                    {...register(`conditions.${conditionIndex}.value`)}
                    readOnly={disabledFields("conditions.value")}
                  />
                </Stack>
              </form>
            </Modal.Card.Body>

            <Modal.Card.Foot>
              <ButtonGroup>
                <Button
                  logLabel="close-publishing-rule-modal-button"
                  onClick={() => closeModal()}
                  variant="secondary"
                >
                  {t(
                    "programBuilder.program.details.publishingRule.button.cancel"
                  )}
                </Button>
                <Button
                  logLabel="save-publishing-rule-button"
                  onClick={() => {
                    addToNewlyAddedList(conditionIndex);
                    setAddedTheNewRule(true);
                  }}
                  variant="primary"
                >
                  {conditions?.[conditionIndex]?.id ||
                  recentlyAdded.includes(conditionIndex)
                    ? t(
                        "programBuilder.program.details.publishingRule.button.edit"
                      )
                    : t(
                        "programBuilder.program.details.publishingRule.button.add"
                      )}
                </Button>
              </ButtonGroup>
            </Modal.Card.Foot>
          </Modal.Card>
        </Modal>
      )}

      <H4>
        {t("programBuilder.program.details.publishingRule.publish.header")}
      </H4>
      <Inline pY="m" gap="xl2">
        <Controller
          name="publishingMode"
          render={({ field }) => (
            <Dropdown
              valueObjectIndexAccessor="name"
              items={publishingModes}
              defaultValue={
                publishingModes.find(x => x.id === publishingMode)?.name
              }
              style={{ width: 200 }}
              onChange={item => field.onChange(item.id)}
              label={t(
                "programBuilder.program.details.publishingRule.publishMethod.label"
              )}
              disabled={disabledFields("PublishingMode")}
            />
          )}
        />
        <Stack spacing="xs">
          <Inline noWrap>
            <Text as="label" htmlFor="conditionCheckDelayInDays">
              {t(
                "programBuilder.program.details.publishingRule.conditionCheck.label"
              )}
            </Text>
            <StyledInput
              style={{ display: "inline-block" }}
              {...register("conditionCheckDelayInDays")}
              id="conditionCheckDelayInDays"
              min={0}
              type="number"
              defaultValue={conditionCheckDelayInDays}
              noScroll
              readOnly={disabledFields("conditionCheckDelayInDays")}
            />
            <Text as="label" htmlFor="conditionCheckDelayInDays">
              {t("programBuilder.program.details.publishingRule.days.label")}
            </Text>
          </Inline>
          {(publishingMode === "PremiumAutoPublish" ||
            publishingMode === "MachineAutoPublish") && (
            <Inline noWrap>
              <Text as="label" htmlFor="publicationDelayInHours">
                {t(
                  "programBuilder.program.details.publishingRule.publicationDelay.label"
                )}
              </Text>
              <StyledInput
                {...register("publicationDelayInHours")}
                id="publicationDelayInHours"
                min={0}
                type="number"
                className="is-checkradio"
                defaultValue={publicationDelayInHours}
                noScroll
                readOnly={disabledFields("publicationDelayInHours")}
              />
              <Text as="label" htmlFor="conditionCheckDelayInDays">
                {t("programBuilder.program.details.publishingRule.hours.label")}
              </Text>
            </Inline>
          )}
        </Stack>

        <Stack spacing="xs">
          {publishingMode !== "Manual" && (
            <Checkbox
              {...register("publicationCancelable")}
              id="publicationCancelable"
              type="checkbox"
              defaultChecked={publicationCancelable}
              readOnly={disabledFields("publicationCancelable")}
              label={t(
                "programBuilder.program.details.publishingRule.publicationCancelable.label"
              )}
            />
          )}

          <Checkbox
            {...register("hideOrderPageUntilPublishingConditionsAreMet")}
            id="hideOrderPageUntilPublishingConditionsAreMet"
            defaultChecked={hideOrderPageUntilPublishingConditionsAreMet}
            readOnly={disabledFields(
              "hideOrderPageUntilPublishingConditionsAreMet"
            )}
            label={t(
              "programBuilder.program.details.publishingRule.hideOrderPageUntilPublishingConditionsAreMet.label"
            )}
          />
        </Stack>
      </Inline>
      <Divider />

      <Table
        pagination={false}
        columns={[
          {
            name: t(
              "programBuilder.program.details.publishingRule.table.attribute.label"
            ),
            selector: "attribute",
            grow: 1,
            cell: (row: ProgramService.ConditionModel, index) => (
              <>
                <And firstChild={index === 0}>{"AND"}</And>
                <Text>{row.attribute}</Text>
              </>
            )
          },
          {
            name: t(
              "programBuilder.program.details.publishingRule.table.operator.label"
            ),
            selector: "operator",
            grow: 1,
            cell: (row: ProgramService.ConditionModel) => (
              <Box pY="m">
                <Text>{row.operator}</Text>
              </Box>
            )
          },
          {
            name: t(
              "programBuilder.program.details.publishingRule.table.value.label"
            ),
            selector: "value",
            grow: 3,
            cell: (row: ProgramService.ConditionModel) => (
              <Box pY="m">
                <Text>{row.value}</Text>
              </Box>
            )
          },
          {
            name: "",
            button: true,
            right: true,
            width: "42px",
            cell: (_, index) => (
              <Button
                logLabel="edit-publishing-rule-button"
                variant="text"
                onClick={() => {
                  setConditionIndex(index);
                  setShowModal(true);
                }}
              >
                <i className="uil uil-pen is-size-4" />
              </Button>
            )
          },
          {
            name: "",
            button: true,
            right: true,
            width: "42px",
            cell: (_, index) => (
              <Button
                logLabel="remove-publishing-rule-button"
                variant="text"
                onClick={() => {
                  remove(index);
                  removeFromNewlyAddedList(conditionIndex);
                }}
                disabled={disabledFields("conditions")}
              >
                <i className="uil uil-trash-alt is-size-4" />
              </Button>
            )
          }
        ]}
        highlightOnHover
        noDataComponent={
          <Box p="xl5">
            {t("programBuilder.program.details.publishingRule.noData.message")}
          </Box>
        }
        data={conditions ?? []}
      />

      <Button
        logLabel="add-publishing-rule-button"
        variant="text"
        onClick={() => {
          append({
            attribute: "",
            value: ""
          } as unknown as ProgramService.ProgramItem);
          setConditionIndex(fields.length);
          setShowModal(true);
        }}
        disabled={disabledFields("conditions")}
      >
        {t("programBuilder.program.details.publishingRule.button.newRule")}
      </Button>

      <Divider />

      <Box>
        <Stack spacing="m">
          <H5>
            {t(
              "programBuilder.program.details.publishingRule.conditionsCheck.header"
            )}
          </H5>
          <Box>
            <Radio
              {...register(`failedConditionAction`)}
              value="CancelPublication"
              id="CancelPublication"
              readOnly={disabledFields("failedConditionAction")}
              label={t(
                "programBuilder.program.details.publishingRule.cancelPublication.label"
              )}
            />
          </Box>

          <Box>
            <Radio
              {...register(`failedConditionAction`)}
              value="RetryConditionsUntilMet"
              id="RetryConditionsUntilMet"
              readOnly={disabledFields("failedConditionAction")}
              label={t(
                "programBuilder.program.details.publishingRule.retryPublication.label"
              )}
            />
          </Box>

          {/* hide for 2 weeks before deleting this option from backend */}

          {/* {publishingMode !== "Manual" && (
            <Box>
              <Radio
                {...register(`failedConditionAction`)}
                value="SendManualOffer"
                id="SendManualOffer"
                readOnly={disabledFields("failedConditionAction")}
                label={t(
                  "programBuilder.program.details.publishingRule.manuelOffer.label"
                )}
              />
            </Box>
          )} */}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <TextArea
          cols={150}
          label={t(
            "programBuilder.program.details.publishingRule.messageToCOPS"
          )}
          {...register("autopublishNote")}
          readOnly={disabledFields("autopublishNote")}
        />
      </Box>
    </Card>
  );
};
